/**
 * Procedural textures shared by the map shaders. Sampling a small tileable texture is far
 * cheaper than evaluating hash noise per pixel, and mipmaps keep it alias-free when zoomed out.
 *
 *  - noise:  256² RGBA, four octaves of smooth tileable value noise (lattice 4 / 8 / 16 / 32),
 *            so one fetch gives a whole fBm stack.
 *  - paper:  512² grey fibre texture (short light/dark strokes + mottling), tileable.
 */
import { CanvasSource, Texture } from 'pixi.js';
import { makeDataTexture } from '../gfx/gl';

function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(seed, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function valueNoise(size: number, lattice: number, seed: number): Float32Array {
  const out = new Float32Array(size * size);
  const cell = size / lattice;
  const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  for (let y = 0; y < size; y++) {
    const gy = y / cell;
    const y0 = Math.floor(gy);
    const ty = fade(gy - y0);
    for (let x = 0; x < size; x++) {
      const gx = x / cell;
      const x0 = Math.floor(gx);
      const tx = fade(gx - x0);
      const x1 = (x0 + 1) % lattice;
      const y1 = (y0 + 1) % lattice;
      const a = hash2(x0, y0, seed);
      const b = hash2(x1, y0, seed);
      const c = hash2(x0, y1, seed);
      const d = hash2(x1, y1, seed);
      out[y * size + x] = (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty;
    }
  }
  return out;
}

export function makeNoiseTexture(): Texture {
  const size = 256;
  const octaves = [
    valueNoise(size, 4, 11),
    valueNoise(size, 8, 23),
    valueNoise(size, 16, 37),
    valueNoise(size, 32, 53),
  ];
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    for (let c = 0; c < 4; c++) data[i * 4 + c] = Math.round(octaves[c][i] * 255);
  }
  return makeDataTexture({ width: size, height: size, data, linear: true, repeat: true, mipmaps: true });
}

/** Tileable paper fibre texture drawn with Canvas2D (grey 128 = neutral). */
export function makePaperTexture(): Texture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d canvas unavailable');
  ctx.fillStyle = 'rgb(128,128,128)';
  ctx.fillRect(0, 0, size, size);
  let seed = 7;
  const rnd = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  // Draw every stroke at the 9 wrapped offsets that can touch the tile, so it tiles seamlessly.
  const wrapped = (draw: (ox: number, oy: number) => void) => {
    for (let oy = -size; oy <= size; oy += size) for (let ox = -size; ox <= size; ox += size) draw(ox, oy);
  };
  // soft mottling
  for (let i = 0; i < 90; i++) {
    const x = rnd() * size;
    const y = rnd() * size;
    const r = 20 + rnd() * 70;
    const light = rnd() < 0.5;
    const a = 0.025 + rnd() * 0.03;
    wrapped((ox, oy) => {
      const g = ctx.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
      g.addColorStop(0, light ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`);
      g.addColorStop(1, 'rgba(128,128,128,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x + ox - r, y + oy - r, r * 2, r * 2);
    });
  }
  // fibres
  ctx.lineCap = 'round';
  for (let i = 0; i < 2600; i++) {
    const x = rnd() * size;
    const y = rnd() * size;
    const ang = rnd() * Math.PI * 2;
    const len = 4 + rnd() * rnd() * 26;
    const bend = (rnd() - 0.5) * len * 0.6;
    const light = rnd() < 0.55;
    const a = 0.05 + rnd() * 0.09;
    const w = 0.5 + rnd() * 0.9;
    const dx = Math.cos(ang) * len;
    const dy = Math.sin(ang) * len;
    wrapped((ox, oy) => {
      if (x + ox < -40 || x + ox > size + 40 || y + oy < -40 || y + oy > size + 40) return;
      ctx.strokeStyle = light ? `rgba(255,255,255,${a})` : `rgba(40,30,20,${a})`;
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.moveTo(x + ox, y + oy);
      ctx.quadraticCurveTo(
        x + ox + dx / 2 - (dy / len) * bend,
        y + oy + dy / 2 + (dx / len) * bend,
        x + ox + dx,
        y + oy + dy,
      );
      ctx.stroke();
    });
  }
  // fine speckle
  const img = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < size * size; i++) {
    const v = (rnd() - 0.5) * 14;
    img.data[i * 4] += v;
    img.data[i * 4 + 1] += v;
    img.data[i * 4 + 2] += v;
  }
  ctx.putImageData(img, 0, 0);
  const source = new CanvasSource({
    resource: canvas,
    addressMode: 'repeat',
    scaleMode: 'linear',
    autoGenerateMipmaps: true,
    mipmapFilter: 'linear',
    alphaMode: 'no-premultiply-alpha',
  });
  return new Texture({ source });
}
